import {
  DisabledButton,
  InputField,
  PageTitle,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  Team,
  TeamRole,
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import React from 'react';
import {
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
} from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { createTeam } from '../../common/api/team';
import { DeveloperOrganizationContextProps } from '../../common/providers/types';
import { addToast } from '../../common/reducers/toasterSlice';

export interface CreateTeamForm {
  name: string;
}

interface CreateTeamProps {
  register: UseFormRegister<CreateTeamForm>;
  handleSubmit: UseFormHandleSubmit<CreateTeamForm>;
  reset: UseFormReset<CreateTeamForm>;
  errors: FieldErrors<CreateTeamForm>;
  isValid: boolean;
}

export const CreateTeam: React.FunctionComponent<CreateTeamProps> = ({
  register,
  handleSubmit,
  reset,
  errors,
  isValid,
}): JSX.Element => {
  const { currentOrg, refreshOrganizations } =
    useOrganization() as DeveloperOrganizationContextProps;
  const { setCurrentTeam } = useTeam();
  const dispatch = useDispatch();

  const onSubmit = async (form: CreateTeamForm) => {
    try {
      const teamId = await createTeam(currentOrg?.id as string, form.name);
      const newToastState = {
        message: `Successfully created team: ${form.name}`,
        isError: false,
      } as ToasterState;
      dispatch(addToast(newToastState));
      setCurrentTeam({
        id: teamId,
        name: form.name,
        role: TeamRole.DEVELOPER_TEAM_ADMIN,
      } as Team);
      refreshOrganizations();
      reset();
    } catch {
      const newToastState = {
        message: `failed to create team: ${form.name}`,
        isError: true,
      } as ToasterState;
      dispatch(addToast(newToastState));
      reset();
    }
  };
  return (
    <form className="pt-os1-spacing" onSubmit={handleSubmit(onSubmit)}>
      <PageTitle className="mb-os1-spacing ">Create a new Team</PageTitle>
      <div className="flex flex-row items-center gap-x-3">
        <div>
          <InputField
            errors={errors}
            inputKey="name"
            register={register}
            registerProps={{
              required: true,
              pattern: {
                value: /^[a-zA-Z1-9 \\#-]{2,20}$/i,
                message: 'Team names are 2 - 20 characters long',
              },
            }}
            title="Create Team"
          />
        </div>
        <div>
          <DisabledButton
            className="h-10 mt-1 rounded-md w-36"
            isActive={!errors.name && isValid}
            type="secondary"
          >
            Create Team
          </DisabledButton>
        </div>
      </div>
      <span className="text-xs text-gray-700">
        You will become the admin of this team
      </span>
    </form>
  );
};
